import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import fr from "./locales/fr.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import de from "./locales/de.json";

// Other locales (pt, sq, ar, zh-Hans, zh-Hant) are kept in ./locales and can be re-enabled here
const DICTS = { fr, de, en, es };
export const LANGS = [
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
];
const DEFAULT_LOCALE = { fr: "fr-CH", de: "de-CH", en: "en-GB", es: "es-ES", pt: "pt-PT", sq: "sq-AL", ar: "ar", "zh-Hans": "zh-CN", "zh-Hant": "zh-TW" };
const LANG_KEY = "rangeo_lang";

function detectLang() {
  const saved = localStorage.getItem(LANG_KEY);
  if (saved && DICTS[saved]) return saved;
  const nav = (navigator.language || "fr").toLowerCase();
  const base = nav.split("-")[0];
  return DICTS[base] ? base : "fr";
}

function localeFor(lang) {
  // Some browsers report non-BCP47 tags (e.g. "en-US@posix") that make Intl throw
  const nav = (navigator.language || "").split(/[@.]/)[0];
  if (!lang.startsWith("zh") && nav.toLowerCase().startsWith(lang)) {
    try { return Intl.getCanonicalLocales(nav)[0]; } catch { /* invalid tag: use default */ }
  }
  return DEFAULT_LOCALE[lang];
}

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);
  const dir = lang === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  const setLang = useCallback((l) => {
    if (!DICTS[l]) return;
    localStorage.setItem(LANG_KEY, l);
    setLangState(l);
  }, []);

  const value = useMemo(() => {
    const dict = DICTS[lang];
    const locale = localeFor(lang);
    const has = (k) => k in dict || k in fr;
    const t = (k, vars) => {
      let s = dict[k] ?? fr[k] ?? k;
      if (vars) Object.entries(vars).forEach(([a, b]) => { s = s.split(`{${a}}`).join(b); });
      return s;
    };
    const fmtDate = (v, opts = { dateStyle: "medium" }) => (v ? new Intl.DateTimeFormat(locale, opts).format(new Date(v)) : "");
    const fmtDateTime = (v) => fmtDate(v, { dateStyle: "medium", timeStyle: "short" });
    const fmtNum = (n) => new Intl.NumberFormat(locale).format(n ?? 0);
    const fmtPrice = (n, currency = "CHF") => new Intl.NumberFormat(locale, { style: "currency", currency }).format(n ?? 0);
    return { lang, dir, setLang, t, has, fmtDate, fmtDateTime, fmtNum, fmtPrice, locale };
  }, [lang, dir, setLang]);

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
